import { createSlice, createSelector } from '@reduxjs/toolkit';

import {
  MapResponseAction,
  RootState,
  LabelType,
  LabelStyle,
} from '../../store';
import { mapSelector, allowLabelsSelector, showLabelsSelector, preferencesSelector } from './map';
import { activeImageOrDuplicateOfSelector, overlayByLabelIdSelector, overlayLabelIdsSelector } from './images';

export interface LabelsState {
  list: LabelType[];
}

export const initialState: LabelsState = { list: [] };

function getLabelStyle(label: LabelType): LabelStyle | undefined {
  return label.preferences?.style;
}

function setStyle(label: LabelType, style: LabelStyle) {
  label.preferences = {
    ...(label.preferences ?? {}),
    style,
  };
}

function clearLabelPreferencesStyle(label: LabelType) {
  if (!label.preferences?.style) return;

  const { style: _style, ...restPreferences } = label.preferences;
  if (Object.keys(restPreferences).length === 0) {
    delete label.preferences;
    return;
  }

  label.preferences = restPreferences;
}

export const labelsSlice = createSlice({
  name: 'labels',
  initialState,
  reducers: {
    // TODO: not used, do we want to allow to remove a Label? (not a label position)
    remove: (state, { payload }) => {
      return {
        ...state,
        list: state.list.filter((label) => label.id !== payload),
      };
    },

    update: (state, { payload }) => {
      const label = state.list.find((l) => l.id === payload.id);
      if (label) {
        // Update existing label
        label.name = payload.name;
      } else {
        // Create new label
        state.list.push(payload);
      }
    },

    setLabelStyle: (state, { payload }: { payload: { id: string; style: LabelStyle } }) => {
      const label = state.list.find((l) => l.id === payload.id);
      if (label) {
        setStyle(label, payload.style);
      }
    },

    applyStyleToSelectedLabels: (state, { payload }: { payload: { ids: string[]; style: LabelStyle } }) => {
      const selectedIds = new Set(payload.ids);
      state.list.forEach((label) => {
        if (selectedIds.has(label.id)) {
          setStyle(label, { ...payload.style });
        }
      });
    },

    mergeStyleIntoSelectedLabels: (state, { payload }: { payload: { ids: string[]; style: LabelStyle } }) => {
      const selectedIds = new Set(payload.ids);
      state.list.forEach((label) => {
        if (selectedIds.has(label.id)) {
          setStyle(label, { ...getLabelStyle(label), ...payload.style });
        }
      });
    },

    clearLabelStyle: (state, { payload: id }: { payload: string }) => {
      const label = state.list.find((l) => l.id === id);
      if (label) {
        clearLabelPreferencesStyle(label);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action): action is MapResponseAction => action.type === 'api/fetchAPIData/fulfilled',
      (state, { payload }) => {
        state.list = payload.labels;
      }
    );
    builder.addMatcher(
      (action): action is MapResponseAction => action.type === 'api/saveAPI/fulfilled',
      (state, { payload }) => {
        const currentLabelsById = new Map(state.list.map((label) => [label.id, label]));
        state.list = payload.labels.map((label) => {
          const currentLabel = currentLabelsById.get(label.id);
          return currentLabel ? { ...label, ...currentLabel } : label;
        });
      }
    );
  },
});

const {
  update,
  remove,
  setLabelStyle,
  applyStyleToSelectedLabels,
  mergeStyleIntoSelectedLabels,
  clearLabelStyle,
} = labelsSlice.actions;
export {
  update as updateLabel,
  remove as removeLabel,
  setLabelStyle,
  applyStyleToSelectedLabels,
  mergeStyleIntoSelectedLabels,
  clearLabelStyle,
};

// Standalone labels; labels shown by an overlay (of any layer) are excluded
export const labelsSelector = createSelector(
  (state: RootState) => state.undoable.present.labels.list,
  mapSelector,
  overlayLabelIdsSelector,
  (labels, iMap, overlayLabelIds) => {
    return iMap.allowLabels ? labels.filter((label) => !overlayLabelIds.has(label.id)) : [];
  }
);

// Labels shown by overlays of the active layer, regardless of allowLabels
export const overlayLabelsSelector = createSelector(
  (state: RootState) => state.undoable.present.labels.list,
  overlayByLabelIdSelector,
  (labels, overlayByLabelId) => labels.filter((label) => overlayByLabelId[label.id]),
);

// Labels not placed in current image
export const notPlacedLabelsSelector = createSelector(
  labelsSelector,
  activeImageOrDuplicateOfSelector,
  (labels, activeImage) => {
    if (!activeImage) return [];
    return labels.filter(
      (l) => !activeImage.labels.map((lPos) => lPos.label).includes(l.id)
    );
  }
);

export const labelsActiveSelector = createSelector(
  allowLabelsSelector,
  showLabelsSelector,
  labelsSelector,
  (allowLabels, showLabels, labels) => allowLabels && showLabels && labels.length > 0,
);

// Returns a function to compute the effective style for a given label,
// merging the map's defaultLabelStyle with the label's own style override.
export const effectiveLabelStyleSelector = createSelector(
  preferencesSelector,
  (preferences) => {
    const defaultStyle = preferences.defaultLabelStyle ?? {};
    return (label: LabelType): LabelStyle => ({ ...defaultStyle, ...label.preferences?.style });
  },
);
